import Usuario from './db/Usuario.js';
import { consultaConTope, TOPE_BARRERA_MS } from './barreraRuta.js';
import cerrarSesionPor401, { tokenDeLaSesion } from './utils/cierreDeSesionPor401.js';

/**
 * #65 · La disponibilidad del usuario y sus permisos, en un solo sitio.
 *
 * POR QUÉ EXISTE (decisión de diseño de Donchurru, 01/10/2026 en la incidencia #65):
 *
 * El menú persistido NO acredita que `Usuario.me()` haya terminado ni que los permisos sean los
 * actuales. `Utils.getUser()` devuelve el usuario de `localStorage` cuando `app.data.user` todavía
 * está vacío, así que al recargar la aplicación con sesión abierta `Utils.getMenu()` contesta al
 * instante con el menú de la sesión ANTERIOR. Quien preguntaba «¿ya está el menú?» para dejar de
 * esperar, dejaba de esperar antes de tiempo y fijaba los permisos con un dato viejo.
 *
 * Y había un segundo agujero, más grande: `app.vue#startSession` solo llamaba a `Usuario.me()`
 * cuando NO había usuario guardado. Con usuario guardado no se refrescaba nunca, así que un cambio
 * de permisos en el servidor no llegaba hasta que alguien volviera a entrar. Esperar al menú era
 * esperar a algo que no iba a pasar.
 *
 * Aquí la pregunta es otra y tiene una sola respuesta: «¿tengo el usuario que ha traído
 * `Usuario.me()` EN ESTA CARGA?». Mientras no lo haya, no hay permiso que valga —ni el del menú
 * persistido ni el respaldo por roles—, y quien necesite decidir con permisos espera a esta
 * promesa. Si la petición falla, falla: no se cae a los permisos antiguos.
 *
 * NO ES UN MECANISMO PARALELO AL DE #49: la barrera del calendario y esta comparten las piezas de
 * `barreraRuta.js`, y quien arranca la carga es siempre este módulo, lo llame `app.vue` al abrir
 * sesión o lo llame una barrera de ruta porque le ha tocado antes.
 *
 * ---------------------------------------------------------------------------------------------
 * EL CICLO DE VIDA DE LA PETICIÓN TIENE DUEÑO, Y ES ESTE MÓDULO [revisión #1711, ronda 6]
 *
 * La primera versión guardaba la promesa de `Usuario.me()` en una variable y la repartía a quien
 * preguntara. Una promesa suelta no se puede abandonar, no se puede caducar y no sabe por qué
 * falló, y de ahí salieron tres fallos con la misma raíz: la petición compartida no tenía dueño.
 *
 *  1. Forzar una recarga ponía la variable a `null`, pero la petición anterior seguía viva y al
 *     llegar pisaba al usuario más nuevo con el más viejo.
 *  2. Si la petición se quedaba colgada, el tope de la barrera cerraba SU espera pero no la
 *     petición, así que «Reintentar» volvía a engancharse a la misma petición muerta, para siempre.
 *  3. Un 401 se trataba como un fallo cualquiera. `Usuario.me()` no pasa por
 *     `DatabaseModel.sendRequest`, así que nadie cerraba la sesión y la pantalla se quedaba en
 *     «sesión no disponible» ofreciendo un reintento que iba a volver a dar 401.
 *
 * Lo que lo cierra, en un solo sitio por el que pasan todos:
 *
 *  - **Cada carga lleva número de serie** (`generacion`). La respuesta de una carga que ya ha sido
 *    sustituida no publica nada: se descarta, igual que `barreraRuta` descarta la respuesta de una
 *    navegación abandonada.
 *  - **Nadie recibe la promesa de la petición**, sino una promesa propia apuntada en la lista de
 *    espera. Si la carga se sustituye, los que esperaban pasan a la nueva en vez de quedarse
 *    colgados de la vieja; así «quiero el usuario de esta carga» siempre contesta el más reciente.
 *  - **El tope vive con la petición**, no con cada uno de los que esperan (`consultaConTope`, la
 *    misma pieza de la #49). Cuando salta, la petición se da por muerta aquí, así que el siguiente
 *    intento es una petición nueva de verdad.
 *  - **El fallo se clasifica**: token rechazado cierra la sesión, como haría `DatabaseModel` si
 *    esta petición pasara por él; lo demás se rechaza y la barrera ofrece reintentar.
 */

/** El usuario que ha traído `Usuario.me()` en esta carga de la aplicación. */
let usuarioFresco = null;

/**
 * Número de serie de la carga vigente. Lo que se compara es la identidad del número, no si hay
 * petición: una carga forzada mientras otra está en vuelo tiene que invalidar a la anterior aunque
 * las dos pregunten exactamente lo mismo.
 */
let generacion = 0;

/** La carga en curso: `{ generacion, cancelar }`. Null si no hay ninguna. */
let intento = null;

/** Quienes esperan el usuario de esta carga, para que diez pantallas no pidan diez veces lo mismo. */
let enEspera = [];

function publicar(user) {
    usuarioFresco = user;
    // Se guarda donde ya lo buscan `Utils.getUser()` y todo lo que cuelga de él, para que el resto
    // de la aplicación vea el usuario fresco sin tener que conocer este módulo.
    if (typeof window != 'undefined' && window.app && window.app.data) {
        window.app.data.user = user;
    }
    try {
        localStorage.setItem('user', JSON.stringify(user));
    } catch (e) {
        // Un localStorage lleno o bloqueado no puede tumbar la sesión: el usuario fresco ya está
        // en memoria, que es de donde lo lee esta carga.
        console.warn('No se ha podido guardar el usuario en localStorage', e);
    }
}

function atenderEsperas(metodo, valor) {
    // Se vacía ANTES de contestar: un `catch` que vuelva a llamar a `cargar()` tiene que apuntarse
    // en la lista siguiente, no recibir dos veces la misma respuesta.
    const pendientes = enEspera;
    enEspera = [];
    pendientes.forEach(function(espera) { espera[metodo](valor); });
}

/**
 * Da por terminada la carga en curso sin contestar a nadie: apaga su tope y deja su respuesta sin
 * efecto. Los que estuvieran esperando siguen esperando, y los atenderá la carga que venga detrás.
 */
function descartarIntento() {
    generacion += 1;
    if (intento) {
        intento.cancelar();
        intento = null;
    }
}

/** El token ya no vale: el mismo criterio que usa `DatabaseModel` con el resto de peticiones. */
function esSesionCaducada(e) {
    if (!e) return false;
    if (typeof e == 'string') return e.indexOf('invalid_token') > -1;
    return e.status === 401 || e.error === 'invalid_token';
}

/**
 * Cierra la sesión como la cerraría cualquier otra petición con un 401, y por la misma pieza:
 * `cierreDeSesionPor401`, que solo la cierra si el 401 es **de la sesión que hay ahora**. Con el
 * token viejo —la cuenta ha cambiado mientras `me()` estaba en vuelo— no se cierra nada, o se
 * echaría a quien acaba de entrar. `removeSession` borra la sesión y el usuario, llama a
 * `olvidar()` y lleva al login.
 *
 * El número de serie de la carga (`generacion`) no cubre esto: cambia cuando alguien pide el
 * usuario otra vez, no cuando cambia la sesión, y entre el cierre y la carga de la cuenta nueva
 * hay un hueco en el que esta carga sigue siendo «la vigente».
 *
 * No se toca el indicador de espera: lo lleva la cuenta de `barreraRuta`, y al navegar al login la
 * barrera que estuviera esperando se cierra sola y lo apaga ella.
 *
 * @param tokenEnviado el token con el que salió esta carga
 */
function cerrarSesionCaducada(tokenEnviado) {
    try {
        cerrarSesionPor401(tokenEnviado);
    } catch (e) {
        console.warn('No se ha podido cerrar la sesión caducada', e);
    }
}

function arrancarIntento() {
    generacion += 1;
    const mia = generacion;
    // Con qué sesión sale esta carga, para que su 401 no cierre otra (ver `cerrarSesionCaducada`).
    const tokenEnviado = tokenDeLaSesion();
    const consulta = consultaConTope(function() { return Usuario.me(); }, TOPE_BARRERA_MS);
    intento = { generacion: mia, cancelar: consulta.cancelar };

    consulta.promesa.then(function(user) {
        // Una carga sustituida no publica: su usuario es más viejo que el que viene de camino.
        if (mia !== generacion) return;
        intento = null;
        if (!user || !user.username) {
            // Un cuerpo que no se sabe leer es un fallo, no un usuario sin permisos: tratarlo
            // como usuario válido dejaría la pantalla decidiendo con un objeto vacío.
            atenderEsperas('reject', { message: 'El servidor no ha devuelto el usuario.' });
            return;
        }
        publicar(user);
        atenderEsperas('resolve', user);
    }, function(e) {
        if (mia !== generacion) return;
        intento = null;
        // El token rechazado se trata aquí porque esta petición no pasa por el manejador de 401 de
        // `DatabaseModel`. Primero se cierra la sesión —así la navegación al login cierra la
        // barrera que esperaba— y después se contesta a quien quede.
        if (esSesionCaducada(e)) cerrarSesionCaducada(tokenEnviado);
        atenderEsperas('reject', e);
    });
}

const UsuarioActualizado = {
    /**
     * Trae el usuario fresco, o devuelve el que ya se trajo. Una sola petición aunque se llame
     * desde varios sitios a la vez.
     *
     * @param {Object} [opciones] `{ forzar: true }` descarta lo que haya —incluida una petición en
     *                 vuelo, que deja de contar— y vuelve a preguntar. Lo usa el arranque de
     *                 sesión, que sí quiere un usuario nuevo.
     */
    cargar: function(opciones) {
        if (opciones && opciones.forzar) {
            usuarioFresco = null;
            descartarIntento();
        }
        if (usuarioFresco) {
            return Promise.resolve(usuarioFresco);
        }
        const promesa = new Promise(function(resolve, reject) {
            enEspera.push({ resolve: resolve, reject: reject });
        });
        if (!intento) arrancarIntento();
        return promesa;
    },

    /** Lo que esperan las barreras de ruta: el usuario de esta carga, pidiéndolo si hace falta. */
    esperar: function() {
        return UsuarioActualizado.cargar();
    },

    /** Si ya se sabe quién es el usuario de esta carga y con qué permisos. */
    estaListo: function() {
        return !!usuarioFresco;
    },

    /** El usuario fresco, o null mientras no lo haya. */
    usuario: function() {
        return usuarioFresco;
    },

    /**
     * Se cierra la sesión: lo que sepamos del usuario deja de valer. Sin esto, el siguiente login
     * en la misma pestaña empezaría dando por bueno el usuario del anterior.
     *
     * A quien estuviera esperando se le contesta que no hay sesión, en vez de dejarlo colgado de
     * una petición que ya no va a publicar nada.
     */
    olvidar: function() {
        usuarioFresco = null;
        descartarIntento();
        atenderEsperas('reject', { message: 'La sesión se ha cerrado.' });
    }
};

export default UsuarioActualizado;
